<template>
  <div
    id="aos-section1"
    class="flex h-[400px] max-h-[900px] items-center justify-center overflow-hidden whitespace-pre-line bg-blue-100 lg:h-[70svh] lg:min-h-[700px]"
  >
    <!-- lg 영역 -->
    <div class="gn-container relative hidden size-full lg:flex">
      <!-- 중앙 텍스트 영역 -->
      <div data-aos="fade-down" data-aos-anchor="#aos-section1" class="z-10 flex flex-1 items-center justify-center">
        <div class="gn-text-3xl text-center font-extrabold">
          <i18n-t keypath="main.section1.title" tag="p" class="whitespace-pre-line">
            <template #grow>
              <span class="text-blue-500"> {{ $t('main.section1.grow') }}</span>
            </template>
            <template #mental>
              <span class="text-blue-500"> {{ $t('main.section1.mental') }}</span>
            </template>
            <template #titleSuffix>
              <span class="font-bold"> {{ $t('main.section1.title-suffix') }}</span>
            </template>
          </i18n-t>
        </div>
      </div>

      <!-- 배경 버블 이미지 -->
      <div
        v-for="(bubble, index) in backgroundBubbles"
        :key="`bubble-${index}`"
        :data-aos="bubble.animate"
        data-aos-anchor="#aos-section1"
        class="absolute aspect-square overflow-hidden rounded-full"
        :style="{
          left: bubble.left,
          right: bubble.right,
          top: bubble.top,
          bottom: bubble.bottom,
          width: bubble.width
        }"
      >
        <OptimizedImage
          base-path="image/home/bg_bubble"
          file-name="bg_bubble"
          :alt="`bg_bubble_${index}`"
          image-class="absolute h-full w-full object-cover"
        />
      </div>

      <!-- 아이들 이미지 -->
      <div
        v-for="(kid, index) in kidsImages"
        :key="`kid-${index}`"
        :data-aos="kid.animate"
        data-aos-anchor="#aos-section1"
        class="absolute aspect-square overflow-hidden rounded-full bg-white"
        :style="{
          left: kid.left,
          right: kid.right,
          top: kid.top,
          bottom: kid.bottom,
          width: kid.width
        }"
      >
        <OptimizedImage
          :base-path="'image/home/index_con01'"
          :file-name="`index_con01_kid0${kid.number}`"
          :alt="`index_con01_kid0${kid.number}`"
          image-class="absolute h-full w-full object-cover opacity-90"
          :localized="index != 2"
        />
      </div>

      <!-- 텍스트 원형 요소 -->
      <div
        v-for="(textCircle, index) in textCircles"
        :key="`text-circle-${index}`"
        :data-aos="textCircle.animate"
        data-aos-anchor="#aos-section1"
        class="absolute flex aspect-square w-44 items-center justify-center rounded-full bg-white/60 backdrop-blur-sm"
        :style="{
          left: textCircle.left,
          top: textCircle.top,
          bottom: textCircle.bottom
        }"
      >
        <p class="gn-text-lg whitespace-pre-line text-center font-bold">
          {{ $t(`main.section1.text-circles.text${textCircle.number}`) }}
        </p>
      </div>
    </div>

    <!-- mg, sm 영역 -->
    <div class="gn-container relative flex size-full flex-col lg:hidden">
      <div data-aos="fade-down" data-aos-anchor="#aos-section1" class="z-10 flex flex-1 items-center justify-center">
        <div class="gn-text-lg text-center font-extrabold">
          <i18n-t keypath="main.section1.title" tag="p" class="whitespace-pre-line">
            <template #grow>
              <span class="text-blue-500"> {{ $t('main.section1.grow') }}</span>
            </template>
            <template #mental>
              <span class="text-blue-500"> {{ $t('main.section1.mental') }}</span>
            </template>
          </i18n-t>
        </div>
      </div>

      <!-- 모바일 배경 버블 이미지 -->
      <div
        v-for="(bubble, index) in mobileBgBubbles"
        :key="`bubble-${index}`"
        :data-aos="bubble.animate"
        data-aos-anchor="#aos-section1"
        class="absolute aspect-square overflow-hidden rounded-full"
        :style="{
          left: bubble.left,
          right: bubble.right,
          top: bubble.top,
          bottom: bubble.bottom,
          width: bubble.width
        }"
      >
        <OptimizedImage
          base-path="image/home/bg_bubble"
          file-name="bg_bubble"
          :alt="`bg_bubble_${index}`"
          image-class="absolute h-full w-full object-cover"
        />
      </div>

      <!-- 모바일 아이들 이미지 -->
      <div
        v-for="(kid, index) in mobileKidsImages"
        :key="`kid-${index}`"
        :data-aos="kid.animate"
        data-aos-anchor="#aos-section1"
        class="absolute aspect-square overflow-hidden rounded-full bg-white"
        :style="{
          left: kid.left,
          right: kid.right,
          top: kid.top,
          bottom: kid.bottom,
          width: kid.width
        }"
      >
        <OptimizedImage
          :base-path="`image/home/index_con01`"
          :file-name="`index_con01_kid0${kid.number}`"
          :alt="`index_con01_kid0${kid.number}`"
          image-class="absolute size-full object-cover opacity-90 "
          :localized="index != 2"
        />
      </div>

      <!-- 모바일 텍스트 원형 요소 -->
      <div
        v-for="(textCircle, index) in mobileTextCircles"
        :key="`text-circle-${index}`"
        :data-aos="textCircle.animate"
        data-aos-anchor="#aos-section1"
        class="absolute flex aspect-square w-24 items-center justify-center rounded-full bg-white/60 backdrop-blur-sm"
        :style="{
          left: textCircle.left,
          top: textCircle.top,
          bottom: textCircle.bottom,
          minWidth: textCircle.minWidth,
          maxWidth: textCircle.maxWidth
        }"
      >
        <p class="gn-text-xs text-center font-bold">
          {{ $t(`main.section1.text-circles.text${textCircle.number}`) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import OptimizedImage from '~/components/common/OptimizedImage.vue'

/** 배경 버블 이미지 데이터 배열 */
const backgroundBubbles = [
  {
    animate: 'fade-up',
    left: 'calc(28% - 40px)',
    top: '-250px',
    width: '450px'
  },
  {
    animate: 'fade-up',
    left: 'calc(8% - 30px)',
    bottom: '-150px',
    width: '250px'
  },
  {
    animate: 'fade-down',
    right: 'calc(30% - 20px)',
    bottom: '-100px',
    width: '240px'
  }
]

/** 아이들 이미지 데이터 배열 */
const kidsImages = [
  {
    number: 3,
    animate: 'fade-up',
    right: 'calc(8% - 40px)',
    bottom: '-60px',
    width: '250px'
  },
  {
    number: 2,
    animate: 'fade-up',
    left: 'calc(10% + 30px)',
    top: '450px',
    width: '230px'
  },
  {
    number: 1,
    animate: 'fade-down',
    left: '60%',
    left: 'calc(65% - 50px)',
    top: '32px',
    width: '250px'
  }
]

/** 텍스트 원형 요소 데이터 배열 */
const textCircles = [
  {
    number: 1,
    animate: 'fade-down',
    left: 'calc(20% - 20px)',
    top: '27px'
  },
  {
    number: 2,
    animate: 'fade-down',
    left: 'calc(35% - 20px)',
    top: '106px'
  },
  {
    number: 3,
    animate: 'fade-up',
    left: 'calc(80% - 30px)',
    top: '87px'
  },

  {
    number: 4,
    animate: 'fade-down',
    left: 'calc(68% - 40px)',
    top: '373px'
  },
  {
    number: 5,
    animate: 'fade-up',
    left: 'calc(48% - 30px)',
    bottom: '30px'
  },
  {
    number: 6,
    animate: 'fade-up',
    left: 'calc(25% - 25px)',
    bottom: '120px'
  }
]

/** 모바일용 배경 버블 이미지 데이터 배열 */
const mobileBgBubbles = [
  {
    animate: 'fade-up',
    left: 'calc(75% - 50px)',
    top: '-52px',
    width: '120px'
  },
  {
    animate: 'fade-down',
    left: 'calc(-15% - 30px)',
    top: '-41px',
    width: '160px'
  },
  {
    animate: 'fade-up',
    left: 'calc(-15% - 20px)',
    top: '281.5px',
    width: '174.5px'
  }
]

/** 모바일용 아이들 이미지 데이터 배열 */
const mobileKidsImages = [
  {
    number: 3,
    animate: 'fade-up',
    left: 'calc(85% - 40px)',
    top: '269px',
    width: '150px'
  },
  {
    number: 2,
    animate: 'fade-up',
    left: 'calc(-15% + 30px)',
    top: '221px',
    width: '130px'
  },
  {
    number: 1,
    animate: 'fade-down',
    left: 'calc(90% - 50px)',
    top: '-32px',
    width: '140px'
  }
]

/** 모바일용 텍스트 원형 요소 데이터 배열 */
const mobileTextCircles = [
  {
    number: 1,
    animate: 'fade-down',
    left: 'calc(5% - 20px)',
    top: '68px'
  },
  {
    number: 2,
    animate: 'fade-down',
    left: 'calc(60% - 20px)',
    top: '33px'
  },
  {
    number: 3,
    animate: 'fade-down',
    left: 'calc(85% - 30px)',
    top: '88px'
  },
  {
    number: 4,
    animate: 'fade-down',
    left: 'calc(85% - 40px)',
    top: '220px'
  },
  {
    number: 5,
    animate: 'fade-up',
    left: 'calc(65% - 30px)',
    top: '301px'
  },
  {
    number: 6,
    animate: 'fade-up',
    left: 'calc(15% - 25px)',
    top: '300px'
  }
]
</script>

<style lang="scss" scoped></style>
